import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { configService } from '../services';

interface PaymentConfigItem {
  _id?: string;
  key: string;
  value: any;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description?: string;
  category: 'financial' | 'email' | 'payment' | 'system' | 'general';
}

const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [installmentsEnabled, setInstallmentsEnabled] = useState(true);
  const [maxInstallments, setMaxInstallments] = useState(12);

  const upsertConfig = async (
    key: string,
    value: any,
    type: PaymentConfigItem['type'],
    description: string,
    category: PaymentConfigItem['category']
  ) => {
    try {
      await configService.update(key, value, description);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        await configService.create({ key, value, type, description, category } as any);
        return;
      }
      throw err;
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await configService.getAll('payment');
        const paymentConfigs = (response?.data?.payment || []) as unknown as PaymentConfigItem[];

        const enabledCfg = paymentConfigs.find((cfg) => cfg.key === 'payment_installments_enabled');
        const maxCfg = paymentConfigs.find((cfg) => cfg.key === 'payment_max_installments');

        if (enabledCfg) setInstallmentsEnabled(Boolean(enabledCfg.value));
        if (maxCfg) setMaxInstallments(Math.max(1, Math.min(Number(maxCfg.value) || 12, 12)));
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar configurações');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const normalizedMax = Math.max(1, Math.min(Number(maxInstallments) || 1, 12));
      setMaxInstallments(normalizedMax);

      await upsertConfig(
        'payment_installments_enabled',
        installmentsEnabled,
        'boolean',
        'Ativa/desativa parcelamento global na plataforma',
        'payment'
      );
      await upsertConfig(
        'payment_max_installments',
        normalizedMax,
        'number',
        'Quantidade máxima de parcelas permitidas globalmente',
        'payment'
      );

      setSuccess('Configurações de parcelamento salvas com sucesso.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Configurações
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Controle global do parcelamento para evitar cobranças indevidas.
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Parcelamento (Global)
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {loading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={installmentsEnabled}
                    onChange={(e) => setInstallmentsEnabled(e.target.checked)}
                  />
                }
                label="Permitir parcelamento na plataforma"
              />

              <TextField
                label="Máximo de parcelas"
                type="number"
                value={maxInstallments}
                onChange={(e) => setMaxInstallments(Number(e.target.value))}
                inputProps={{ min: 1, max: 12 }}
                helperText="Valor entre 1 e 12"
                disabled={!installmentsEnabled}
              />

              <Box>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  {saving ? <CircularProgress size={22} color="inherit" /> : 'Salvar configurações'}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SettingsPage;

